import axios from 'axios';
import { KjedeTreNode } from 'components/KjedePicker';

import {
  KjedeDto,
  NewKjedeCommand,
  UpdateKjedeCommand,
  GetKjedeByIdRequest,
  GetKjedeByIdResponse,
  FlyttKjedeTilNyttInnkjopCommand,
  ParentKjedePathResponse,
  KjedeType,
} from './types';

import { getKjederegAdminApiUrl } from '../../';

const baseUrl = getKjederegAdminApiUrl() + '/kjeder';

const getKjedeById = async (request: GetKjedeByIdRequest): Promise<GetKjedeByIdResponse> => {
  const httpResponse = await axios.get(
    `${baseUrl}/${request.id}?type=${encodeURI(request.kjedeType)}`,
  );
  const kjedeResponse: GetKjedeByIdResponse = httpResponse.data;

  // we need this for kjedebreadcrumb
  kjedeResponse.hierarchy = kjedeResponse.parentTree.map((o) => ({
    nr: o.kjedenr,
    name: o.name,
    enhetId: o.enhetId,
    id: o.id,
  }));
  kjedeResponse.hierarchy.push({
    nr: kjedeResponse.kjedenr,
    name: kjedeResponse.kjedenavn,
    enhetId: kjedeResponse.enhetID,
    id: kjedeResponse.kjedeID,
  });
  return kjedeResponse;
};

const getRegionTre = async (query?: {
  includeDisbanded?: boolean;
  includeFuture?: boolean;
}): Promise<KjedeTreNode[]> => {
  const params = new URLSearchParams();
  if (query?.includeDisbanded) {
    params.append('includeDisbanded', String(query.includeDisbanded));
  }
  if (query?.includeFuture) {
    params.append('includeFuture', String(query.includeFuture));
  }
  const queryString = params.toString();
  const url = queryString ? `${baseUrl}/regional/tree?${queryString}` : `${baseUrl}/regional/tree`;
  const response = await axios.get(url);
  return response.data;
};

const getMarkedsTre = async (query?: {
  includeDisbanded?: boolean;
  includeFuture?: boolean;
}): Promise<KjedeTreNode[]> => {
  const params = new URLSearchParams();
  if (query?.includeDisbanded) {
    params.append('includeDisbanded', String(query.includeDisbanded));
  }
  if (query?.includeFuture) {
    params.append('includeFuture', String(query.includeFuture));
  }
  const queryString = params.toString();
  const url = queryString
    ? `${baseUrl}/markedsforing/tree?${queryString}`
    : `${baseUrl}/markedsforing/tree`;
  const response = await axios.get(url);
  return response.data;
};

const getAntallaktivemedlemmerforkjedeplussunderkjeder = async (
  kjedeId: number,
  kjedeType: string,
): Promise<number> =>
  axios
    .get(
      `${baseUrl}/${kjedeId}/antallaktivemedlemmerforkjedeplussunderkjeder?kjedeType=${encodeURI(
        kjedeType,
      )}`,
    )
    .then((response) => response.data);

const updateKjede = async (id: number, command: UpdateKjedeCommand) =>
  axios.put(`${baseUrl}/${id}`, command).then((response) => response.data);

const addKjede = async (command: NewKjedeCommand): Promise<KjedeDto> =>
  axios.post(baseUrl, command).then((response) => response.data);

const deleteKjede = async (id: number) =>
  axios.delete(`${baseUrl}/${id}`).then((response) => response.data);

const flyttKjedeTilNyttInnkjop = async (
  kjedeId: number,
  command: FlyttKjedeTilNyttInnkjopCommand,
) => {
  return axios.post(`${baseUrl}/${kjedeId}/flytt-til-nytt-innkjop`, command);
};

//regionalLeafByPostnummer
const regionalLeafByPostnummer = async (parentKjedenr: string, postnummer: string) => {
  return axios.get(`${baseUrl}/${parentKjedenr}/regionalLeafByPostnummer/${postnummer}`);
};

// markedskjedeLeafBySalgsareal
const markedskjedeLeafBySalgsareal = async (parentKjedenr: string, salgsareal: number) => {
  return axios.get(`${baseUrl}/${parentKjedenr}/markedLeafBySalgsareal/${salgsareal}`);
};

// getParentKjedePath - henter parent kjede path/hierarchy
const getParentKjedePath = async (
  kjedenr: string,
  kjedeType: KjedeType,
): Promise<ParentKjedePathResponse> => {
  return axios
    .get(`${baseUrl}/byKjedeNr/${kjedenr}?type=${encodeURI(kjedeType.toString())}`)
    .then((response) => response.data);
};

export default {
  getRegionTre,
  getMarkedsTre,
  getAntallaktivemedlemmerforkjedeplussunderkjeder,
  getKjedeById,
  updateKjede,
  addKjede,
  deleteKjede,
  flyttKjedeTilNyttInnkjop,
  regionalLeafByPostnummer,
  markedskjedeLeafBySalgsareal,
  getParentKjedePath,
};
